import { useEffect, useState } from "react";
import { useDeleteAsk, deletionRequested } from "@/lib/deleteApproval";
import { Link, useSearchParams } from "react-router-dom";
import { listRecordings, listScripts, uploadRecording, deleteRecording, listLeads } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { UploadCloud, Plus, Trash2, Loader2 } from "lucide-react";

export default function Recordings() {
  const askDelete = useDeleteAsk();
  const [recs, setRecs] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [params] = useSearchParams();
  const presetLead = params.get("lead") || "";

  const refresh = async () => {
    const [r, s, l] = await Promise.all([listRecordings(), listScripts(), listLeads()]);
    setRecs(r);
    setScripts(s);
    setLeads(l);
  };

  useEffect(() => {
    refresh();
    if (presetLead) setOpen(true);
    const t = setInterval(() => {
      setRecs((cur) => {
        if ((cur || []).some((c) => ["pending", "transcribing", "scoring"].includes(c.status))) {
          listRecordings().then(setRecs);
        }
        return cur;
      });
    }, 3500);
    return () => clearInterval(t);
  }, []);

  const onDelete = async (id) => {
    const reason = askDelete("this recording");
    if (reason === null) return;
    try { if (deletionRequested(await deleteRecording(id, reason))) return; }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete it"); return; }
    toast.success("Recording deleted");
    refresh();
  };

  return (
    <div className="space-y-6" data-testid="recordings-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">All recordings</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Sales calls</h2>
          <p className="text-sm text-zinc-400 mt-1">Upload a recording and Cadence will transcribe & score it against your script.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger data-testid="recordings-dialog-trigger-59" asChild>
            <Button
              className="bg-blue-600 hover:bg-blue-500 rounded-full px-5"
              data-testid="upload-recording-btn"
              disabled={scripts.length === 0}
            >
              <Plus className="h-4 w-4 mr-1.5" /> Upload call
            </Button>
          </DialogTrigger>
          <UploadDialog
            scripts={scripts}
            leads={leads}
            presetLead={presetLead}
            onDone={() => {
              setOpen(false);
              refresh();
            }}
          />
        </Dialog>
      </div>

      {scripts.length === 0 && (
        <div className="card-surface p-5 border-amber-500/30 bg-amber-500/5">
          <div className="text-sm text-amber-300">
            You need at least one script before uploading a recording.{" "}
            <Link data-testid="recordings-link-84" to="/scripts" className="underline hover:text-amber-200">
              Add a script
            </Link>
            .
          </div>
        </div>
      )}

      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
              <th className="px-5 py-3 font-medium">Call</th>
              <th className="px-5 py-3 font-medium">Script</th>
              <th className="px-5 py-3 font-medium">Uploaded</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Score</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {recs.map((r) => (
              <tr key={r.id} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] transition-colors">
                <td className="px-5 py-4">
                  <Link to={`/recordings/${r.id}`} className="font-medium hover:text-blue-400 transition-colors" data-testid={`recording-title-${r.id}`}>
                    {r.title}
                  </Link>
                  <div className="text-xs text-zinc-500 mt-0.5 truncate max-w-xs">{r.filename}</div>
                </td>
                <td className="px-5 py-4 text-zinc-400">{r.script_title}</td>
                <td className="px-5 py-4 text-zinc-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-4"><StatusBadge status={r.status} /></td>
                <td className="px-5 py-4 text-right font-display font-bold text-lg">
                  {r.overall_score ?? <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    onClick={() => onDelete(r.id)}
                    className="text-zinc-500 hover:text-rose-400 transition-colors"
                    data-testid={`delete-recording-${r.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {recs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center text-sm text-zinc-500">
                  No recordings yet. Click <span className="text-zinc-300">Upload call</span> to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function UploadDialog({ scripts, leads, presetLead, onDone }) {
  const [title, setTitle] = useState("");
  const [scriptId, setScriptId] = useState(scripts[0]?.id || "");
  const [leadId, setLeadId] = useState(presetLead || "");
  const [file, setFile] = useState(null);
  const [prog, setProg] = useState(0);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an audio file");
    if (!scriptId) return toast.error("Pick a script");
    if (!title.trim()) return toast.error("Give it a title");
    setBusy(true);
    try {
      await uploadRecording(title, scriptId, file, setProg, leadId || null);
      toast.success("Uploaded! Transcribing now…");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      setProg(0);
    }
  };

  return (
    <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-lg" data-testid="upload-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">Upload a sales call</DialogTitle>
      </DialogHeader>
      <form data-testid="recordings-form-176" onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label htmlFor="rec-title" className="text-xs uppercase tracking-[0.2em] text-zinc-500">Call name</Label>
          <Input
            id="rec-title"
            data-testid="upload-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Acme Corp — Discovery call"
            className="bg-[#0a0a0a] border-[#262626] mt-2"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Compare against</Label>
          <Select data-testid="recordings-select-190" value={scriptId} onValueChange={setScriptId}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-2" data-testid="upload-script-select">
              <SelectValue placeholder="Pick a script" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
              {(scripts || []).map((s) => (
                <SelectItem data-testid="recordings-select-item-196" key={s.id} value={s.id} className="focus:bg-[#1a1a1a]">{s.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Link to lead (optional)</Label>
          <Select data-testid="recordings-select-203" value={leadId || "none"} onValueChange={(v) => setLeadId(v === "none" ? "" : v)}>
            <SelectTrigger className="bg-[#0a0a0a] border-[#262626] mt-2" data-testid="upload-lead-select">
              <SelectValue placeholder="No lead" />
            </SelectTrigger>
            <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-h-72">
              <SelectItem data-testid="recordings-select-item-208" value="none" className="focus:bg-[#1a1a1a]">— No lead —</SelectItem>
              {(leads || []).map((l) => (
                <SelectItem data-testid="recordings-select-item-210" key={l.id} value={l.id} className="focus:bg-[#1a1a1a]">
                  {l.name} {l.phone && <span className="text-zinc-500">· {l.phone}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Audio file</Label>
          <label
            htmlFor="audio-file"
            className="mt-2 flex flex-col items-center justify-center border border-dashed border-[#333] rounded-xl px-6 py-8 cursor-pointer hover:border-blue-500/60 hover:bg-[#0d0d0d] transition-colors"
            data-testid="upload-dropzone"
          >
            <UploadCloud className="h-6 w-6 text-zinc-500 mb-2" />
            <div className="text-sm text-zinc-300">{file ? file.name : "Choose an audio file"}</div>
            <div className="text-[11px] text-zinc-500 mt-1">mp3, wav, m4a, webm, ogg, flac · long calls split automatically</div>
            <input
              id="audio-file"
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.webm,.mp4,.mpga,.mpeg"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file-input"
            />
          </label>
        </div>

        {busy && (
          <div>
            <Progress value={prog} className="h-1.5 bg-[#1a1a1a]" />
            <div className="text-[11px] text-zinc-500 mt-1.5">Uploading {prog}%</div>
          </div>
        )}

        <DialogFooter>
          <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-500 rounded-full" data-testid="upload-submit-btn">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading…</> : "Upload & score"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
